import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Home, 
  MapPin, 
  Trophy, 
  Wallet, 
  User, 
  LogOut, 
  Menu,
  X,
  Shield,
  Calendar,
  Users,
  Medal,
  Table,
  Building2,
  Banknote,
  ClipboardList,
  Swords,
  Star,
  MoreHorizontal
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { generateInitials, formatCurrency } from '../lib/utils';
import NotificationBell from './NotificationBell';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_matchfinder-25/artifacts/wk49ezk2_Logo.jpg";

export const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Navigation items based on user role
  const getNavItems = () => {
    if (user?.role === 'field_owner') {
      // Field owners only see their management pages
      return [
        { path: '/owner', label: 'Dashboard', icon: Home },
        { path: '/owner/fields', label: 'Meine Plätze', icon: Building2 },
        { path: '/owner/calendar', label: 'Kalender', icon: Calendar },
        { path: '/owner/bookings', label: 'Buchungen', icon: ClipboardList },
        { path: '/owner/earnings', label: 'Einnahmen', icon: Banknote },
      ];
    } else if (user?.role === 'admin') {
      // Admin sees admin navigation
      return [
        { path: '/admin', label: 'Dashboard', icon: Home },
        { path: '/admin/results', label: 'Ergebnisse', icon: Trophy },
        { path: '/admin/players', label: 'Spieler', icon: Users },
        { path: '/admin/field-owners', label: 'Feldbesitzer', icon: Building2 },
        { path: '/admin/teams', label: 'Teams', icon: Users },
        { path: '/admin/tournaments', label: 'Turniere', icon: Trophy },
        { path: '/admin/leagues', label: 'Ligen', icon: Table },
      ];
    } else {
      // Regular players see full navigation
      return [
        { path: '/dashboard', label: 'Dashboard', icon: Home },
        { path: '/map', label: 'Karte', icon: MapPin },
        { path: '/matches', label: 'Spiele', icon: Calendar },
        { path: '/teams', label: 'Teams', icon: Users },
        { path: '/favorites', label: 'Stammspieler', icon: Star },
        { path: '/challenges', label: 'Herausforderungen', icon: Swords },
        { path: '/tournaments', label: 'Turniere', icon: Trophy },
        { path: '/leagues', label: 'Ligen', icon: Table },
      ];
    }
  };

  const navItems = getNavItems();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur-lg border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="Tunis Kick" className="h-10 w-10 rounded-full object-cover" />
            <span className="font-display font-bold text-xl text-tunis-blue hidden sm:block">
              Tunis Kick
            </span>
          </Link>

          {/* Desktop Navigation */}
          {isAuthenticated && (
            <div className="hidden lg:flex items-center gap-1 mx-4 flex-1 justify-center">
              {navItems.slice(0, 6).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={`flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-tunis-blue text-white'
                        : 'text-tunis-blue hover:bg-tunis-blue/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden xl:inline">{item.label}</span>
                  </Link>
                );
              })}
              {/* More menu for remaining items */}
              {navItems.length > 6 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-tunis-blue hover:bg-tunis-blue/10 px-2.5 py-2 rounded-full">
                      <MoreHorizontal className="w-4 h-4" />
                      <span className="hidden xl:inline ml-1.5">Mehr</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {navItems.slice(6).map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <DropdownMenuItem key={item.path} asChild>
                          <Link
                            to={item.path}
                            className={`flex items-center gap-2 ${isActive ? 'bg-tunis-blue/10 text-tunis-blue' : ''}`}
                          >
                            <Icon className="w-4 h-4" />
                            {item.label}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}

          {/* Right Side */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {isAuthenticated ? (
              <>
                {/* Wallet Balance - only for players */}
                {user?.role !== 'field_owner' && user?.role !== 'admin' && (
                  <Link
                    to="/wallet"
                    data-testid="wallet-balance-btn"
                    className="hidden sm:flex items-center gap-2 px-4 py-2 bg-tunis-teal/10 text-tunis-teal rounded-full font-semibold text-sm hover:bg-tunis-teal/20 transition-colors"
                  >
                    <Wallet className="w-4 h-4" />
                    {formatCurrency(user?.wallet_balance || 0)}
                  </Link>
                )}
                
                {/* Field Owner Balance */}
                {user?.role === 'field_owner' && (
                  <Link
                    to="/owner/earnings"
                    className="hidden sm:flex items-center gap-2 px-4 py-2 bg-tunis-teal/10 text-tunis-teal rounded-full font-semibold text-sm hover:bg-tunis-teal/20 transition-colors"
                  >
                    <Banknote className="w-4 h-4" />
                    {formatCurrency(user?.wallet_balance || 0)}
                  </Link>
                )}

                {/* Notification Bell - Only for Players and Field Owners */}
                {user?.role !== 'admin' && (
                  <NotificationBell />
                )}

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                      <Avatar className="h-10 w-10 border-2 border-tunis-blue/20">
                        <AvatarImage src={user?.avatar_url} alt={user?.name} />
                        <AvatarFallback className="bg-tunis-blue text-white font-bold">
                          {generateInitials(user?.name || 'U')}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email || user?.phone}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {user?.role === 'field_owner' ? (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/profile" className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Profil
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/owner" className="flex items-center gap-2">
                            <Home className="w-4 h-4" />
                            Dashboard
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/owner/fields" className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            Meine Plätze
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/owner/bookings" className="flex items-center gap-2">
                            <ClipboardList className="w-4 h-4" />
                            Buchungen
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/owner/earnings" className="flex items-center gap-2">
                            <Banknote className="w-4 h-4" />
                            Einnahmen
                          </Link>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/profile" className="flex items-center gap-2" data-testid="menu-profile">
                            <User className="w-4 h-4" />
                            Profil
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/wallet" className="flex items-center gap-2" data-testid="menu-wallet">
                            <Wallet className="w-4 h-4" />
                            Wallet
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/teams" className="flex items-center gap-2" data-testid="menu-teams">
                            <Users className="w-4 h-4" />
                            Meine Teams
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/leaderboard" className="flex items-center gap-2" data-testid="menu-leaderboard">
                            <Medal className="w-4 h-4" />
                            Rangliste
                          </Link>
                        </DropdownMenuItem>
                        {user?.role === 'admin' && (
                          <DropdownMenuItem asChild>
                            <Link to="/admin" className="flex items-center gap-2" data-testid="menu-admin">
                              <Shield className="w-4 h-4" />
                              Admin
                            </Link>
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Abmelden
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Mobile Menu Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-btn"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="font-semibold" data-testid="login-btn">
                    Anmelden
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary px-6" data-testid="register-btn">
                    Registrieren
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isAuthenticated && mobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-tunis-blue text-white'
                      : 'text-tunis-blue hover:bg-tunis-blue/10'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export const MobileNav = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) return null;

  // Different nav items based on role - shortened labels for mobile
  const getNavItems = () => {
    if (user?.role === 'field_owner') {
      return [
        { path: '/owner', icon: Home },
        { path: '/owner/fields', icon: Building2 },
        { path: '/owner/calendar', icon: Calendar },
        { path: '/owner/bookings', icon: ClipboardList },
      ];
    } else if (user?.role === 'admin') {
      return [
        { path: '/admin', icon: Home },
        { path: '/admin/players', icon: Users },
        { path: '/admin/field-owners', icon: Building2 },
        { path: '/admin/reports', icon: Table },
      ];
    } else {
      return [
        { path: '/dashboard', icon: Home },
        { path: '/fields', icon: MapPin },
        { path: '/bookings', icon: Calendar },
        { path: '/teams', icon: Users },
        { path: '/profile', icon: User },
      ];
    }
  };

  const navItems = getNavItems();

  return (
    <div className="mobile-dock lg:hidden" data-testid="mobile-nav">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center gap-1 px-2 transition-all ${
              isActive ? 'text-tunis-teal' : 'text-white/70 hover:text-white'
            }`}
            data-testid={`mobile-nav-${item.path.slice(1)}`}
          >
            <Icon className={`w-6 h-6 ${isActive ? 'scale-110' : ''}`} />
            {item.label && <span className="text-[10px] font-medium">{item.label}</span>}
          </Link>
        );
      })}
    </div>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pb-20 lg:pb-8 safe-area-bottom">{children}</main>
      <MobileNav />
    </div>
  );
};

export default Layout;
